import { create } from 'zustand';
import { useState, useEffect } from 'react';

// Reading pane display mode
export type ReadingPaneMode = 'sidebar' | 'popup';

// Board view mode
export type BoardViewMode = 'board' | 'list';

interface UiState {
  // Selected mail for reading pane
  selectedMailId: string | null;
  selectedMailAccountId: string | null;
  selectedMailProvider: string | null;
  
  // Active board tab
  activeBoardId: string | null;
  
  // Modal states
  isBoardEditorOpen: boolean;
  editingBoardId: string | null;
  
  // Reading pane
  isReadingPaneOpen: boolean;
  readingPaneMode: ReadingPaneMode;
  
  // Board view mode
  boardViewMode: BoardViewMode;
  
  // Actions
  selectMail: (mailId: string | null, accountId?: string | null, provider?: string | null) => void;
  setActiveBoard: (boardId: string | null) => void;
  openBoardEditor: (boardId?: string | null) => void;
  closeBoardEditor: () => void;
  toggleReadingPane: (open?: boolean) => void;
  setReadingPaneMode: (mode: ReadingPaneMode) => void;
  setBoardViewMode: (mode: BoardViewMode) => void;
}

// Load persisted settings from localStorage
const getPersistedSettings = (): { readingPaneMode: ReadingPaneMode; boardViewMode: BoardViewMode } => {
  try {
    const stored = localStorage.getItem('kolumi-ui-settings');
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        readingPaneMode: parsed.readingPaneMode || 'sidebar',
        boardViewMode: parsed.boardViewMode || 'board',
      };
    }
  } catch {
    // Ignore parse errors
  }
  return { readingPaneMode: 'sidebar', boardViewMode: 'board' };
};

const persistSettings = (settings: Partial<{ readingPaneMode: ReadingPaneMode; boardViewMode: BoardViewMode }>) => {
  try {
    const existing = JSON.parse(localStorage.getItem('kolumi-ui-settings') || '{}');
    localStorage.setItem('kolumi-ui-settings', JSON.stringify({ ...existing, ...settings }));
  } catch {
    // Ignore storage errors
  }
};

const initialSettings = getPersistedSettings();

export const useUiStore = create<UiState>()((set) => ({
  selectedMailId: null,
  selectedMailAccountId: null,
  selectedMailProvider: null,
  activeBoardId: null,
  isBoardEditorOpen: false,
  editingBoardId: null,
  isReadingPaneOpen: false,
  readingPaneMode: initialSettings.readingPaneMode,
  boardViewMode: initialSettings.boardViewMode,

  selectMail: (mailId, accountId = null, provider = null) =>
    set({
      selectedMailId: mailId,
      selectedMailAccountId: accountId,
      selectedMailProvider: provider,
      isReadingPaneOpen: !!mailId,
    }),

  setActiveBoard: (boardId) => set({ activeBoardId: boardId }),

  openBoardEditor: (boardId = null) =>
    set({
      isBoardEditorOpen: true,
      editingBoardId: boardId,
    }),

  closeBoardEditor: () =>
    set({
      isBoardEditorOpen: false,
      editingBoardId: null,
    }),

  toggleReadingPane: (open) =>
    set((state) => ({
      isReadingPaneOpen: open ?? !state.isReadingPaneOpen,
      selectedMailId: open === false ? null : state.selectedMailId,
      selectedMailAccountId: open === false ? null : state.selectedMailAccountId,
      selectedMailProvider: open === false ? null : state.selectedMailProvider,
    })),

  setReadingPaneMode: (mode) => {
    persistSettings({ readingPaneMode: mode });
    set({ readingPaneMode: mode });
  },

  setBoardViewMode: (mode) => {
    persistSettings({ boardViewMode: mode });
    set({ boardViewMode: mode });
  },
}));

// Hook to check if we're on mobile
export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};
